import { ActionIcon, Badge, Group, Menu, Paper, Stack, Text } from '@mantine/core';
import {
  IconDotsVertical,
  IconFileText,
  IconPencil,
  IconPlus,
  IconRobot,
  IconRoute,
  IconTrash,
} from '@tabler/icons-react';
import { Link } from '@tanstack/react-router';

type PersonaCardProps = {
  id: string;
  name: string;
  description: string;
  documentCount: number;
  outputCount: number;
  onDelete: () => void;
};

export function PersonaCard({ id, name, description, documentCount, outputCount, onDelete }: PersonaCardProps) {
  return (
    <Paper p="lg" radius="lg" withBorder component={Link} to={`/personas/${id}`} style={{ textDecoration: 'none' }}>
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm">
            <IconRobot size={20} stroke={1.5} style={{ color: 'var(--mantine-color-violet-filled)' }} />
            <Text fw={600}>{name}</Text>
          </Group>
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" onClick={(e) => e.preventDefault()}>
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item leftSection={<IconPencil size={14} />} component={Link} to={`/personas/${id}`}>
                Edit
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                color="red"
                onClick={(e) => {
                  e.preventDefault();
                  onDelete();
                }}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>

        {!!description && (
          <Text size="sm" c="dimmed" lineClamp={2}>
            {description}
          </Text>
        )}

        <Group gap="xs">
          <Badge size="sm" variant="light" leftSection={<IconFileText size={12} />}>
            {documentCount} doc{documentCount !== 1 ? 's' : ''}
          </Badge>
          <Badge size="sm" variant="light" leftSection={<IconRoute size={12} />}>
            {outputCount} output{outputCount !== 1 ? 's' : ''}
          </Badge>
        </Group>
      </Stack>
    </Paper>
  );
}

export function NewPersonaCard({ onClick }: { onClick: () => void }) {
  return (
    <Paper
      p="lg"
      radius="lg"
      withBorder
      onClick={onClick}
      style={{
        borderStyle: 'dashed',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 140,
        cursor: 'pointer',
      }}
    >
      <Stack align="center" gap="xs">
        <IconPlus size={24} style={{ color: 'var(--mantine-color-dimmed)' }} />
        <Text size="sm" c="dimmed">
          Create new persona
        </Text>
      </Stack>
    </Paper>
  );
}
